import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ReservationFlowProductDetail } from 'src/interfaces/reservation_flow';
import { TicketBookPurchaseFormValues } from 'src/pages/[publicId]/ticket_books/[productPublicId]/purchase';
import { RSAnchor } from 'src/reservation-page/RSAnchor';
import { RSButton } from 'src/reservation-page/RSButton';
import { RSDescriptionList } from 'src/reservation-page/description-list/RSDescriptionList';
import { RSDescriptionListItem } from 'src/reservation-page/description-list/RSDescriptionListItem';
import { RSDetailHeader } from 'src/reservation-page/detail-header/RSDetailHeader';
import { RSDetailHeaderButton } from 'src/reservation-page/detail-header/RSDetailHeaderButton';
import { buildSiteUrl } from 'src/utils/request-url';

type TicketBookConfirmTemplateProps = {
  productDetail: ReservationFlowProductDetail;
  onNext: (values: TicketBookPurchaseFormValues) => void;
  onBack: () => void;
};

export const TicketBookConfirmTemplate: FC<TicketBookConfirmTemplateProps> = ({
  productDetail,
  onNext,
  onBack,
}) => {
  const intl = useIntl();
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useFormContext<TicketBookPurchaseFormValues>();

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <div className="z-5 sticky top-0">
        <RSDetailHeader
          title={intl.formatMessage({
            defaultMessage: '回数券購入',
            id: 'ggBoAy',
            description:
              'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
          })}
          leftButton={
            <RSDetailHeaderButton variant="close" onClick={onBack}>
              {intl.formatMessage({
                defaultMessage: '戻る',
                id: 'ozq7mZ',
                description:
                  'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
              })}
            </RSDetailHeaderButton>
          }
        />
      </div>

      <div className="flex justify-center">
        <div className="pc:px-4 pc:pb-4 pc:pt-4 max-w-[496px] grow px-2.5 pb-6 pt-3">
          <div className="mb-3">
            <div className="rs-font text-24 text-gray-1000 pc:text-28 font-bold">
              {intl.formatMessage({
                defaultMessage: 'この内容で購入しますか？',
                id: 'npwOuA',
                description:
                  'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
              })}
            </div>
          </div>

          <div className="mb-4">
            <div className="flex items-center">
              {productDetail.image_url && (
                <div className="mr-1.5 flex-none">
                  <img
                    className="box-border block h-[56px] rounded-[6px]"
                    src={productDetail.image_url}
                    alt=""
                  />
                </div>
              )}

              <div className="flex-auto">
                <div className="rs-font text-16 text-gray-1000 pc:text-15 font-bold">
                  {productDetail.name}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <RSDescriptionList>
              <RSDescriptionListItem
                term={intl.formatMessage({
                  defaultMessage: '合計金額',
                  id: 'SNz/8O',
                  description:
                    'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
                })}
                description={
                  <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                    {intl.formatMessage(
                      {
                        defaultMessage: '{priceString}（税込）',
                        id: 'zIV6aC',
                        description:
                          'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
                      },
                      {
                        priceString: intl.formatNumber(productDetail.price, {
                          style: 'currency',
                          currency: 'JPY',
                        }),
                      },
                    )}
                  </div>
                }
              />
              {(() => {
                if (
                  productDetail.ticket_count === null ||
                  productDetail.expiration_month === null
                ) {
                  return null;
                }

                return (
                  <>
                    <RSDescriptionListItem
                      term={intl.formatMessage({
                        defaultMessage: '利用可能数',
                        id: '1F+AYm',
                        description:
                          'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
                      })}
                      description={
                        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                          {intl.formatMessage(
                            {
                              defaultMessage: '{ticketCount}回',
                              id: 'a6omUZ',
                              description:
                                'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
                            },
                            {
                              ticketCount: productDetail.ticket_count,
                            },
                          )}
                        </div>
                      }
                    />
                    <RSDescriptionListItem
                      term={intl.formatMessage({
                        defaultMessage: '有効期限',
                        id: '/5P3FC',
                        description:
                          'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
                      })}
                      description={
                        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                          {intl.formatMessage(
                            {
                              defaultMessage: '{expirationMonth}ヶ月',
                              id: 'Wh14/9',
                              description:
                                'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
                            },
                            {
                              expirationMonth: productDetail.expiration_month,
                            },
                          )}
                        </div>
                      }
                    />
                  </>
                );
              })()}
              <RSDescriptionListItem
                term={intl.formatMessage({
                  defaultMessage: 'お支払い方法',
                  id: 'foZEQC',
                  description:
                    'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
                })}
                description={
                  <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                    {intl.formatMessage({
                      defaultMessage: 'クレジットカード決済',
                      id: '2NGZwO',
                      description:
                        'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
                    })}
                  </div>
                }
              />
            </RSDescriptionList>
          </div>

          <div className="mb-4">
            <p className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
              {intl.formatMessage(
                {
                  defaultMessage:
                    '購入完了すると、{terms}に同意したとみなします。キャンセルその他の販売条件は、{sctl}をあわせてご確認ください。',
                  id: 'WCRu07',
                  description:
                    'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
                },
                {
                  terms: (
                    <RSAnchor
                      href="https://stores.fun/legal/reserve-user-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.formatMessage({
                        defaultMessage: '利用規約',
                        id: 'yEJR54',
                        description:
                          'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
                      })}
                    </RSAnchor>
                  ),
                  sctl: (
                    <RSAnchor
                      href={buildSiteUrl(`/legal_notice`)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.formatMessage({
                        defaultMessage: '特定商取引法に基づく表示',
                        id: 'D/RdEk',
                        description:
                          'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
                      })}
                    </RSAnchor>
                  ),
                },
              )}
            </p>
          </div>

          <div>
            <RSButton
              variant="large"
              isBlock
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              {intl.formatMessage({
                defaultMessage: '購入確定',
                id: 'sEkKES',
                description:
                  'src/templates/merchants/purchase/TicketBookConfirmTemplate/index.tsx',
              })}
            </RSButton>
          </div>
        </div>
      </div>
    </form>
  );
};
